import { useStoreSelector } from '@ui/hooks/useStoreSelector';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import useOpportunitiesStore from '../store/useOpportunitiesStore';
import {
    DESKTOP_LIMIT,
    MOBILE_LIMIT,
    OPPORTUNITY_STATUS,
} from '@components/OpportunityListing/utils/constants';
import { validateFilters } from '@components/OpportunityListing/utils/utils';

const useSyncFilters = () => {
    const searchParams = useSearchParams();
    const router = useRouter();
    const { fetchOpportunities, fetchOpportunitiesStats, status, setStatus, isAuthorized } =
        useStoreSelector(useOpportunitiesStore, (state) => ({
            fetchOpportunities: state.fetchOpportunities,
            fetchOpportunitiesStats: state.fetchOpportunitiesStats,
            status: state.status,
            setStatus: state.setStatus,
            isAuthorized: state.isAuthorized,
        }));

    // Store the last filters to prevent duplicate calls
    const lastFiltersRef = useRef(null);

    const handleSearchParamsChange = () => {
        let queryParams = Object.fromEntries(searchParams.entries());
        // Validate the query parameters
        if (queryParams) {
            const {validatedFilters, hasChanges} = validateFilters(queryParams);
            queryParams = validatedFilters;
            if (hasChanges) {
                router.replace(
                    {
                        pathname: router.pathname,
                        query: validatedFilters,
                    },
                    undefined,
                    { shallow: true }
                );
            }
        }
        // Prevent duplicate calls if filters haven't changed
        if (
            JSON.stringify(lastFiltersRef.current) !==
            JSON.stringify(queryParams)
        ) {
            lastFiltersRef.current = queryParams;
            if (router?.pathname?.includes('closed')) {
                setStatus(OPPORTUNITY_STATUS.CLOSED); // Update status in the store based on status in url
            } else if (status !== OPPORTUNITY_STATUS.OPEN) {
                setStatus(OPPORTUNITY_STATUS.OPEN);
            }
            if (typeof window !== 'undefined')
                fetchOpportunities({
                    ...queryParams,
                    limit:
                        window.innerWidth < 1280 ? MOBILE_LIMIT : DESKTOP_LIMIT,
                });
        }
    }

    useEffect(() => {
        if (
            (router.pathname.includes('closed') &&
                status === OPPORTUNITY_STATUS.CLOSED) ||
            (!router.pathname.includes('closed') &&
                status === OPPORTUNITY_STATUS.OPEN)
        ) {
            fetchOpportunitiesStats(status === OPPORTUNITY_STATUS.OPEN);
        }
    }, [status]);

    useEffect(() => {
        handleSearchParamsChange();
    }, [searchParams.toString()]);

    const updateQueryParams = (paramsToUpdate) => {
        const params = new URLSearchParams(searchParams.toString());

        Object.entries(paramsToUpdate).forEach(([key, value]) => {
            if (value) {
                params.set(key, value);
            } else {
                params.delete(key);
            }
        });

        router.push(
            {
                pathname: router.pathname,
                query: Object.fromEntries(params.entries()),
            },
            undefined,
            { scroll: false, shallow: true }
        );
    };

    const clearAllFilters = (queryParams) => {
        const filteredQueryParams = Object.fromEntries(
            Object.entries(queryParams).filter(
                ([key, value]) => value !== undefined
            )
        );

        router.push(
            {
                pathname: router.pathname,
                query: {
                    ...filteredQueryParams,
                },
            },
            undefined,
            { scroll: false, shallow: true }
        );
    };

    return { updateQueryParams, clearAllFilters };
};

export default useSyncFilters;
